import { RefObject, useEffect, useRef, useState } from "react";

interface UseInViewOptions {
	/** Report "in view" straight away, without observing at all. */
	disabled?: boolean;
	rootMargin?: string;
}

/**
 * One-shot viewport detection: flips to `true` the first time the element is intersecting
 * and then stops observing. Reports `true` immediately when disabled, or when the browser
 * has no IntersectionObserver — content must never depend on this to become readable.
 */
export function useInView<T extends HTMLElement>({ disabled = false, rootMargin }: UseInViewOptions = {}): [RefObject<T>, boolean] {
	const ref = useRef<T>(null);
	const [inView, setInView] = useState(() => disabled || typeof IntersectionObserver === "undefined");

	useEffect(() => {
		if (inView || !ref.current) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) return;
				setInView(true);
				observer.disconnect();
			},
			rootMargin ? { rootMargin } : undefined,
		);

		observer.observe(ref.current);
		return () => observer.disconnect();
	}, [inView, rootMargin]);

	return [ref, inView];
}
